import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../react-reduxs/configure_store";

import {
  getToken,
  loginOut,
  setCompanyId,
  setToken,
  setUserId,
} from "../../react-reduxs/slices/auth/auth_slice";
import { useTranslation } from "react-i18next";
import { I18nPagePropertyEnum } from "../../i18n/i18n_page_property";
import i18n from "i18next";
import "../../enums/languages/language.enum";
import { useNavigate } from "react-router-dom";
import { AuthService } from "../../services/auth/auth.service";
import { LoginModel } from "../../services/auth/models/login.model";
import { UserTypeEnum } from "../../enums/user_type.enum";
import "./login.css";
import {
  getCompanyList,
  setCompanyList,
  setCompanyPublic,
} from "../../react-reduxs/slices/companies/company_slice";
import "react-toastify/dist/ReactToastify.css";
import ThemeCard from "@theme/ui-components/cards/ThemeCard";
import ThemeDynamicForm from "@theme/ui-components/forms/ThemeDynamicForm";
import { RefType } from "@theme/ui-components/forms/form-field-type";

import ThemeButton from "@theme/ui-components/buttons/ThemeButton";
import ThemeSelect from "@theme/ui-components/selects/ThemeSelect";
import { DropdownChangeEvent } from "primereact/dropdown";
import { LoginFields } from "./LoginFormField";
import { CompanyModel } from "../../services/companies/models/company.model";
import { DataView } from "primereact/dataview";

const LoginPage = () => {
  const formRef = useRef<RefType>();
  const authService = useMemo(() => new AuthService(), []);
  const [pageGo, setPageGo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const token = useSelector(getToken);
  const companyList = useSelector(getCompanyList);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();
  const [languageList, setLanguageList] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

  const languageChange = useCallback((language: string) => {
    setSelectedLanguage(language);
    i18n.changeLanguage(language);
  }, []);

  useEffect(() => {
    if (i18n.options.resources) {
      const resourceKeys = Object.keys(i18n.options.resources);
      setLanguageList(resourceKeys);
    }

    return () => {};
  }, []);

  useEffect(() => {
    if (token !== "" && pageGo) {
      const decodedToken: any = JSON.parse(atob(token.split(".")[1]));
      dispatch(
        setUserId(
          decodedToken[
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
          ] ?? "",
        ),
      );
      if (decodedToken?.userType === UserTypeEnum.Waiter) {
        navigate("/waiter");
      } else if (decodedToken?.userType === UserTypeEnum.Admin) {
        navigate("/admin");
      } else if (decodedToken?.userType === UserTypeEnum.CompanyManager) {
        navigate("/compManager");
      }
    } else if (token === "") {
      setPageGo(false);
      dispatch(loginOut());
      dispatch(setCompanyPublic(undefined));
      dispatch(setCompanyList([]));
      dispatch(setCompanyId(""));
    }
    return () => {};
  }, [navigate, pageGo, token]);

  const gridItem = (company: CompanyModel) => {
    return (
      <div className="col-12 sm:col-6 lg:col-12 xl:col-4 p-2" key={company.id}>
        <div className="p-4 border-1 surface-border surface-card border-round">
          <div className="flex flex-column align-items-center gap-3 py-5">
            <img
              className="w-9 shadow-2 border-round"
              src={
                company?.logo.length > 0
                  ? company.logo.toApiImage()
                  : "link-logo.png".toApiImage()
              }
              alt={company.title}
            />
            <div className="text-lg font-bold">
              {company.title} {company.subheading}
            </div>
          </div>
          <div className="flex align-items-center justify-content-between">
            <ThemeButton
              className="w-full"
              key={"select-company"}
              onClick={() => {
                dispatch(setCompanyId(company.id));
                dispatch(setCompanyPublic(company));
                setPageGo(true);
              }}
              label="Firmayı Seç"
            />
          </div>
        </div>
      </div>
    );
  };
  const onFinish = useCallback(
    async (values: LoginModel) => {
      setIsLoading(true);
      try {
        let response = await authService.login(values);
        if (response?.companies) {
          if (response.companies.length > 0) {
            dispatch(setCompanyList(response.companies));
            if (response.companies.length === 1) {
              dispatch(setCompanyId(response.companies[0].id));
              dispatch(setCompanyPublic(response.companies[0]));
              setPageGo(true);
            }
          } else {
            setPageGo(true);
          }
        }
        if (response?.token) {
          let decodeToken = JSON.parse(atob(response.token.split(".")[1]));
          if (decodeToken) {
            if (decodeToken.companyId) {
              dispatch(setCompanyId(decodeToken.companyId));
            }
          }
          dispatch(setToken(response.token));
        }
      } catch (error) {
        // Token ayarlanamadı, uygun bir şekilde işlem yapılabilir (örneğin, kullanıcıya bir hata mesajı gösterilebilir)
      } finally {
        setIsLoading(false);
      }
    },
    [authService, dispatch],
  );

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundColor: "rgb(232 240 254 / 57%)",
      }}
    >
      <ThemeCard className="login-content">
        <div className="grid w-full" style={{ height: 50, marginBottom: 10 }}>
          <div className="col-1">
            <img width={50} src="./images/Backlink.png" alt="" />
          </div>
          <div
            style={{
              paddingLeft: 10,
            }}
            className="col"
          >
            <h1
              className="text-center"
              style={{
                lineHeight: 0.5,
                fontWeight: "normal",
                fontFamily: "BoldGroovy",
              }}
            >
              Link {t(I18nPagePropertyEnum.loginTitle)}
            </h1>
          </div>
        </div>
        {companyList.length === 0 ? (
          <div>
            <ThemeDynamicForm
              fieldClassName="field col-12"
              ref={formRef}
              onFinish={(values) => onFinish(values)}
              fields={LoginFields}
              initialValues={{ userName: "", password: "" }}
            />
            <ThemeSelect
              disabled
              value={selectedLanguage}
              onChange={(e: DropdownChangeEvent) =>
                languageChange(e.target.value)
              }
              optionLabel="label"
              optionValue="value"
              options={languageList.map((lang) => ({
                label: lang.toText() + " Dil beta sürümdür",
                value: lang,
              }))}
            />
            <br />
            <div className="btn-content">
              <ThemeButton
                loading={isLoading}
                disabled={isLoading}
                icon={
                  <i className="fa-duotone fa-arrow-right-from-arc mr-1"></i>
                }
                onClick={() => {
                  formRef.current?.submitForm();
                }}
                label={`${t(I18nPagePropertyEnum.loginLogin)}`}
              />
            </div>
          </div>
        ) : (
          <div style={{ textAlign: "center" }}>
            <p className="text-base">Firma Seçiniz </p>
            <DataView value={companyList} itemTemplate={gridItem} />
          </div>
        )}
      </ThemeCard>
    </div>
  );
};

export default LoginPage;
